import _ from 'lodash'
import axios from 'axios'
import { createAction, createReducer } from 'redux-act'
import { pendingTask, begin, end } from 'react-redux-spinner'
import l8n from 'l8n'
import { baseUrl } from 'scripts/env'

const REDUCER = 'app'
const NS = `@@${REDUCER}/`

const _setLoading = createAction(`${NS}SET_LOADING`)

export const USER_STATUS_NOT_ACTIVE = 0
export const USER_STATUS_ACTIVATION_REQUESTED = 1
export const USER_STATUS_ACTIVE = 2

export const setLocale = createAction(`${NS}SET_LOCALE`)
export const setFrom = createAction(`${NS}SET_FROM`)
export const setLoginFormHidden = createAction(`${NS}SET_LOGIN_FORM_HIDDEN`)
export const setUser = createAction(`${NS}SET_USER`)
export const removeUser = createAction(`${NS}REMOVE_USER`)
export const setToken = createAction(`${NS}SET_TOKEN`)
export const toggleLogAs = createAction(`${NS}TOGGLE_LOG_AS`)
export const setUpdatingContent = createAction(`${NS}SET_UPDATING_CONTENT`)
export const setActiveDialog = createAction(`${NS}SET_ACTIVE_DIALOG`)
export const deleteDialogForm = createAction(`${NS}DELETE_DIALOG_FORM`)
export const addSubmitForm = createAction(`${NS}ADD_SUBMIT_FORM`)
export const deleteSubmitForm = createAction(`${NS}DELETE_SUBMIT_FORM`)
export const setLayoutState = createAction(`${NS}SET_LAYOUT_STATE`)
export const acceptCookies = createAction(`${NS}ACCEPT_COOKIES`)
export const refuseCookies = createAction(`${NS}REFUSE_COOKIES`)

export const getCurrentUser = state => state.app.logAs.enabled ? state.app.logAs.user : state.app.user

export const getAccessToken = state => state.app.logAs.enabled ? state.app.logAs.token : state.app.token

export const setLoading = isLoading => {
  const action = _setLoading(isLoading)
  action[pendingTask] = isLoading ? begin : end
  return action
}

export const resetLoginFromVisibility = () => (dispatch, getState) => {
  const state = getState()
  if (state.pendingTasks === 0 && state.app.isLoginFormHidden) {
    dispatch(setLoginFormHidden(false))
  }
  return Promise.resolve()
}

// Получение информации о пользователе
export const getUser = () => async (dispatch) => {
  //console.log("Valadating Hash and User status")
  const response = await axios({ 
      url: `${baseUrl}/user/current`
  });
  let user = _.get(response, 'data.user', false)
  if (user === false) {
      return false
  }

  if (window.jivo_api) {
    window.jivo_api.setUserToken(user.client_token);
  } else {
    window.jivo_onLoadCallback = function() {
      window.jivo_api.setUserToken(user.client_token);
    };
  }

  dispatch(setUser(user))
  return true
}

const codesEU = [
  'BE', 'EL', 'LT', 'PT',
  'BG', 'ES', 'LU', 'RO',
  'CZ', 'FR', 'HU', 'SI',
  'DK', 'HR', 'MT', 'SK',
  'DE', 'IT', 'NL', 'FI',
  'EE', 'CY', 'AT', 'SE',
  'IE', 'LV', 'PL', 'UK',
]

export const getIsGeoEU = () => async () => {
  const response = await axios({
    url: `${baseUrl}/geo`,
  })

  const countryCode = _.get(response, 'data.country_code', false)
  if (countryCode === false) {
    return false
  }

  return countryCode && codesEU.indexOf(countryCode) !== -1
};

const tokenState = {
  payload: null,
  modified: null,
}

const userState = {
  id: null,
  display_name: null,
  role: null,
  site_url: null,
  email: null,
  status: USER_STATUS_NOT_ACTIVE,
  details: false,
  currency: 'RUB',
  pricelist_minimum_partner_withdrawal: 10000000,
  vat: false
}

const getValueFromStorage = (storageKey, defaultValue) => {
  const valueFromStorage = JSON.parse(window.localStorage.getItem(storageKey))
  if (! valueFromStorage) {
    return defaultValue
  }

  return valueFromStorage
}

const getStateFromStorage = (storageKey, defaultConfig) => {
  try {
    const stateFromStorage = getValueFromStorage(storageKey, {})

    return {
      ...defaultConfig,
      ...stateFromStorage,
    }
  } catch (error) {
    return defaultConfig
  }
}

const initialState = {
  acceptCookies: getValueFromStorage('app.acceptCookies', false),

  locale: {
    value: l8n.defaultLocale,
    url: false
  },

  from: '',
  isUpdatingContent: false,
  isLoading: false,
  activeDialog: '',
  dialogForms: {},
  submitForms: {},
  isLoginFormHidden: false,

  user: getStateFromStorage('app.User', userState),
  token: getStateFromStorage('app.Token', tokenState),

  logAs: {
    enabled: false,
    user: userState,
    token: tokenState
  },

  layoutState: {
    isMenuTop: false,
    menuMobileOpened: false,
    menuCollapsed: false,
    menuShadow: true,
    themeLight: true,
    squaredBorders: false,
    borderLess: true,
    fixedWidth: false,
    settingsOpened: false,
  }
}

export const getCurrencySymbol = (currency) => {
  const map = {
    RUB: '₽',
    USD: '$',
    BRL: 'R$',
    EUR: '€',
    INR: 'INR',
    TRY: '₺',
    IDR: 'Rp',
    COP: '$',
    ARS: '$',
    CLP: '$',
    MXN: '$',
    KES: 'KSh',
    ZAR: 'R',
    PEN: 'S/.'
  }
  return _.get(map, currency, '?')
}

export default createReducer(
  {
    [setLocale]: (state, locale) => ({ ...state, locale }),
    [setFrom]: (state, from) => ({ ...state, from }),
    [_setLoading]: (state, isLoading) => ({ ...state, isLoading }),
    [setLoginFormHidden]: (state, isLoginFormHidden) => ({ ...state, isLoginFormHidden }),
    
    [setLayoutState]: (state, param) => {
      const layoutState = { ...state.layoutState, ...param }
      const newState = { ...state, layoutState }
      window.localStorage.setItem('app.layoutState', JSON.stringify(layoutState))
      return newState
    },

    [setUpdatingContent]: (state, isUpdatingContent) => ({ ...state, isUpdatingContent }),
    [setUser]: (state, params) => {
      var user = {}
      if (state.logAs.enabled) {
        user = { ...state.logAs.user, ...params }
        user.currency_symbol = getCurrencySymbol(user.currency)
        return { ...state, logAs: { ...state.logAs, user }}
      } else {
        user = { ...state.user, ...params }
        user.currency_symbol = getCurrencySymbol(user.currency)
        window.localStorage.setItem('app.User', JSON.stringify(user))
        return { ...state, user }
      }
    },
    [removeUser]: (state) => {
      window.localStorage.removeItem('app.User')
      window.localStorage.removeItem('app.Token')
      return { ...state, user: userState, logAs: { ...state.logAs, user: userState, enabled: false }}
    },
    [setToken]: (state, token) => {
      const newState = { payload: token}
      if (state.logAs.enabled) {
        return { ...state, logAs: { ...state.logAs, token: newState }}
      } else {
        window.localStorage.setItem('app.Token', JSON.stringify(newState))
        return { ...state, token: newState }
      }
    },
    [acceptCookies]: (state) => {
      window.localStorage.setItem('app.acceptCookies', JSON.stringify(true))
      return { ...state, acceptCookies: true }
    },
    [refuseCookies]: (state) => {
      window.localStorage.setItem('app.acceptCookies', JSON.stringify(false))
      return { ...state, acceptCookies: false }
    },
    [toggleLogAs]: (state, enabled) => {
      return { ...state, logAs: { ...state.logAs, enabled} }
    },
    [setActiveDialog]: (state, activeDialog) => {
      const result = { ...state, activeDialog }
      if (activeDialog !== state.activeDialog) {
        const id = activeDialog
        result.dialogForms = { ...state.dialogForms, [id]: true }
      }
      return result
    },
    [deleteDialogForm]: (state, id) => {
      const dialogForms = { ...state.dialogForms }
      delete dialogForms[id]
      return { ...state, dialogForms }
    },
    [addSubmitForm]: (state, id) => {
      const submitForms = { ...state.submitForms, [id]: true }
      return { ...state, submitForms }
    },
    [deleteSubmitForm]: (state, id) => {
      const submitForms = { ...state.submitForms }
      delete submitForms[id]
      return { ...state, submitForms }
    },
  },
  initialState,
)



// WEBPACK FOOTER //
// ./src/ducks/app.js